
import React, { Fragment, Suspense, useContext, useEffect, useRef, useState } from 'react'
import classnames from "classnames"
import { Route, Routes, Navigate, useNavigate } from 'react-router-dom'
import { SpinnerComponent } from 'react-element-spinner'
import { ErrorBoundary } from 'react-error-boundary'
import { Button, Form, FormGroup, Input, InputGroup, InputGroupText, Modal, ModalBody, ModalHeader } from 'reactstrap'
import { useTranslation } from 'react-i18next'
import sweetalert from 'sweetalert2'
import { library } from "@fortawesome/fontawesome-svg-core"
import { faUser, faLockOpen, faEyeSlash, faEye } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import ResizeDetectorWrapper from '../hooks/ResizeDetectorWrapper'
import { useThemeContext } from '../contexts/ThemeProvider'
import ApplicationHeader from '../components/Headers/ApplicationHeader'
import ApplicationSidebar from '../components/Sidebars/ApplicationSidebar'
import ApplicationFooter from '../components/Footers/ApplicationFooter'
import ErrorFallback from '../components/Errors/ErrorFallback'
import RouteGuard from '../components/Security/RouteGuard'
import Enumeration from '../enumeration'
import { SessionContext } from '../contexts/SessionProvider'
import AuthenticationService from '../services/AuthenticationService'
import { subscribe, unsubscribe } from '../events/customEvents'
import BusinessDashboard from '../views/dashboards/BusinessDashboard'
import AuctionDurations from '../views/administration/AuctionDurations'
import AuctionTemplatesListing from '../views/auction-templates/AuctionTemplatesListing'
import AuctionTemplateDetail from '../views/auction-templates/AuctionTemplateDetail'
import AuctionsSearch from '../views/auctions/business/AuctionsSearch'
import AuctionsListing from '../views/auctions/business/AuctionsListing'
import AuctionDetail from '../views/auctions/business/components/AuctionDetail'
import AuctionBids from '../views/auctions/business/components/AuctionBids'
import AuctionVehicle from '../views/auctions/business/components/AuctionVehicle'
import AuctionOptions from '../views/auctions/business/components/AuctionOptions'
import AuctionCondition from '../views/auctions/business/components/AuctionCondition'
import AuctionDamages from '../views/auctions/business/components/AuctionDamages'
import AuctionPictures from '../views/auctions/business/components/AuctionPictures'
import AuctionDisclosures from '../views/auctions/business/components/AuctionDisclosures'
import BusinessVehiclesSearch from '../views/vehicles/business/BusinessVehiclesSearch'
import BusinessVehiclesListing from '../views/vehicles/business/BusinessVehiclesListing'
import BusinessSalesReport from '../views/reports/BusinessSalesReport'
import ListingReport from '../views/reports/ListingReport'
import DealerDashboard from '../views/dashboards/DealerDashboard'
import DealerAuctionsListing from '../views/auctions/dealers/DealerAuctionsListing'
import DealerVehicleDetail from '../views/vehicles/dealers/DealerVehicleDetail'
import VehicleWatchList from '../views/watchlists/VehicleWatchList'
import DealerOwnedVehicleListing from '../views/vehicles/dealers/DealerOwnedVehicleListing'

const SecureLayout = ({ children }) =>
{
	const [controller, dispatch] = useThemeContext()
	const {
		colorScheme,
		enableFixedHeader,
		enableFixedSidebar,
		enableFixedFooter,
		enableClosedSidebar,
		closedSmallerSidebar,
		enableMobileMenu,
		enablePageTabsAlt
	} = controller
	const [isBusy, setIsBusy] = useState(false)
	const sessionContext = useContext(SessionContext)
	const [isSessionExpiredModalOpen, setIsSessionExpiredModal] = useState(false)
	const { t } = useTranslation()
	const navigate = useNavigate()
	const [modalIsBusy, setModalIsBusy] = useState(false)
	const [loginRequest, setLoginRequest] = useState({ userName: '', password: '' })
	const [alertProps, setAlertProps] = useState({ title: '', message: '' })
	const [showAlert, setShowAlert] = useState(false)
	const [showPassword, setShowPassword] = useState(false)
	
	useEffect(() =>
	{
		subscribe('sessionExpired', setIsSessionExpiredModal)
		
		return () =>
		{
			unsubscribe('sessionExpired', setIsSessionExpiredModal)
		}
	}, [])
	
	const handleError = (error, info) =>
	{
	
	}
	
	const toggleSessionExpiredModal = () =>
	{
		if (isSessionExpiredModalOpen && !sessionContext.loginSession.isAuthenticated)
		{
			sessionContext.setSession(sessionContext.defaultSession)
			navigate('/public/login')
		}
	}
	
	const closeSessionExpiredModal = () =>
	{
		sessionContext.setSession(sessionContext.defaultSession)
		setIsSessionExpiredModal(false)
		navigate('/public/login')
	}
	
	const handleDisplayAuthFailure = (title, message) =>
	{
		sweetalert.fire({
			title: title,
			text: message,
			icon: 'error',
			timer: 3000,
			timerProgressBar: true,
			showConfirmButton: false
		});
	}
	
	const handleExtendSession = (e) =>
	{
		e.preventDefault()
		
		setModalIsBusy(true)
		AuthenticationService
			.authenticateUser(loginRequest)
			.then((result) =>
			{
				setModalIsBusy(false)
				
				let loginSession = result.data
				if (loginSession.isAuthenticated)
				{
					setLoginRequest({ password: '', userName: '' })
					sessionContext.setSession(loginSession)
					setIsSessionExpiredModal(false)
				}
				else
				{
					if (loginSession.authenticationResult !== undefined)
					{
						let failedMessage = t('Login.Failed_BadCredentials')
						handleDisplayAuthFailure(t('Login.Failed_Title'), failedMessage)
					}
				}
			})
			.catch((error) =>
			{
				setModalIsBusy(false)
			})
	}
	
	const handleUserNameChange = (e) =>
	{
		const { name, value} = e.target
		setLoginRequest({ ...loginRequest, [name]: value })
	}
	
	const handlePasswordChange = (e) =>
	{
		const { name, value } = e.target
		setLoginRequest({ ...loginRequest, [name]: value })
	}
	
	return (
		<>
			<ResizeDetectorWrapper>
				{(width) => (
					<Fragment>
						<SpinnerComponent
							loading={ isBusy }
							position="global"
							color="#3578e5"
							secondaryColor="#f3f3f3"
							backgroundColor="#d3d3d3"
							spinnerType="circle-dots"
						/>
						<div
							className={classnames(
								"app-container app-theme-" + colorScheme,
								{ "fixed-header": enableFixedHeader },
								{ "fixed-sidebar": enableFixedSidebar || width < 1250 },
								{ "fixed-footer": enableFixedFooter },
								{ "closed-sidebar": enableClosedSidebar || width < 1250 },
								{ "closed-sidebar-mobile": closedSmallerSidebar || width < 1250 },
								{ "sidebar-mobile-open": enableMobileMenu },
								{ "body-tabs-shadow-btn": enablePageTabsAlt }
							)}>
							<Fragment>
								<ApplicationHeader />
								<div className="app-main">
									<ApplicationSidebar />
									<div className="app-main__outer">
										<div className="app-main__inner">
											<ErrorBoundary FallbackComponent={ ErrorFallback } onError={ handleError }>
												<Routes>
													{/*
														Administrator (CDLSI) only routes. Guarded so a dealer
														cannot reach them by typing the URL directly.
													*/}
													<Route element={ <RouteGuard allowedGroups={ [Enumeration.UserGroups.Administrators] } /> }>
														<Route path="/business-dashboard" element={ <BusinessDashboard /> } />
														<Route path="/auction-durations" element={ <AuctionDurations /> } />
														<Route path="/auction-templates-listing" element={ <AuctionTemplatesListing /> } />
														<Route path="/auction-template-detail" element={ <AuctionTemplateDetail /> } />
														<Route path="/auctions-search" element={ <AuctionsSearch /> } />
														<Route path="/auctions-listing" element={ <AuctionsListing /> } />
														<Route path="/auction-detail" element={ <AuctionDetail /> } />
														<Route path="/auction-bids" element={ <AuctionBids /> } />
														<Route path="/auction-vehicle" element={ <AuctionVehicle /> } />
														<Route path="/auction-options" element={ <AuctionOptions /> } />
														<Route path="/auction-condition" element={ <AuctionCondition /> } />
														<Route path="/auction-damages" element={ <AuctionDamages /> } />
														<Route path="/auction-pictures" element={ <AuctionPictures /> } />
														<Route path="/auction-disclosures" element={ <AuctionDisclosures /> } />
														<Route path="/business-vehicles-search" element={ <BusinessVehiclesSearch /> } />
														<Route path="/business-vehicles-listing" element={ <BusinessVehiclesListing /> } />
														<Route path="/business-sales-report" element={ <BusinessSalesReport /> } />
														<Route path="/business-listing-report" element={ <ListingReport /> } />
													</Route>
													{/*
														Dealer only routes. Guarded so an administrator-scoped
														session cannot land on dealer-specific views and a dealer
														is restricted to exactly these pages.
													*/}
													<Route element={ <RouteGuard allowedGroups={ [Enumeration.UserGroups.Dealers] } /> }>
														<Route path="/dealer-dashboard" element={ <DealerDashboard /> } />
														<Route path="/dealer-auction-listing" element={ <DealerAuctionsListing /> } />
														<Route path="/dealer-vehicle-detail" element={ <DealerVehicleDetail /> } />
														<Route path="/dealer-vehicle-watchlist" element={ <VehicleWatchList /> } />
														<Route path="/owned-vehicles" element={ <DealerOwnedVehicleListing /> } />
													</Route>
												</Routes>
											</ErrorBoundary>
										</div>
										<ApplicationFooter />
									</div>
								</div>
							</Fragment>
						</div>
					</Fragment>
				)}
			</ResizeDetectorWrapper>
			<Modal
				isOpen={ isSessionExpiredModalOpen }
				toggle={ () => toggleSessionExpiredModal() }
			>
				<SpinnerComponent
					loading={ modalIsBusy }
					position="centered"
					color="#3578e5"
					secondaryColor="#f3f3f3"
					backgroundColor="#d3d3d3"
					spinnerType="circle-dots"
				/>
				<ModalHeader toggle={ closeSessionExpiredModal } charCode="Y">
					{ t('Common.Session_Expired') }
				</ModalHeader>
				<ModalBody>
					<Form role="form" onSubmit={ handleExtendSession }>
						<FormGroup className={classnames('mb-3' )} >
							<InputGroup className="input-group-merge input-group-alternative">
								<InputGroupText>
									<FontAwesomeIcon icon={ faUser } />
								</InputGroupText>
								<Input
									autoFocus
									name="userName"
									placeholder={ t('Login.Username_Placeholder') }
									type="text"
									value={ loginRequest.userName }
									onChange={ handleUserNameChange }
								/>
							</InputGroup>
						</FormGroup>
						<FormGroup className={classnames()} >
							<InputGroup className="input-group-merge input-group-alternative">
								<InputGroupText>
									<FontAwesomeIcon icon={ faLockOpen } />
								</InputGroupText>
								<Input
									id="password"
									name="password"
									placeholder={ t('Login.Password_Placeholder') }
									type="password"
									value={ loginRequest.password }
									onChange={ handlePasswordChange }
								/>
								<InputGroupText>
									<FontAwesomeIcon icon={ showPassword ? faEyeSlash : faEye } style={{ cursor: 'pointer' }} onClick={ () => setShowPassword(!showPassword) } />
								</InputGroupText>
							</InputGroup>
						</FormGroup>
						<div className="text-center">
							<Button type="submit" className="btn-block" color="primary">
								{ t('Login.Sign_In_Button') }
							</Button>
						</div>
					</Form>
				</ModalBody>
			</Modal>
		</>
	)
}

export default SecureLayout
